import { buildAuthHeader } from "./authEmail";

export type FunnelEventType =
  | "login"
  | "email_capture"
  | "self_test"
  | "landing_view"
  | "scenario_click"
  | "input_submit"
  | "crucible_load"
  | "question_asked"
  | "signin_complete";

export type FunnelEventPayload = {
  type: FunnelEventType;
  email: string;
  route?: string;
  source?: string;
  reason?: string;
  sessionId?: string;
  timestamp?: string;
  meta?: Record<string, unknown>;
};

export type FunnelEvent = {
  ts: string;
  type: FunnelEventType | string;
  email: string;
  route?: string;
  source?: string;
  reason?: string;
  session_id?: string;
  meta?: Record<string, unknown>;
};

export type FunnelSummary = {
  total: number;
  last24h: number;
  byType: Record<string, number>;
  latest?: FunnelEvent;
};

type SendFunnelEventOptions = {
  throwOnError?: boolean;
};

function apiBaseUrl() {
  return import.meta.env.VITE_API_BASE_URL || "";
}

function requestHeaders() {
  return {
    "Content-Type": "application/json",
    ...buildAuthHeader(),
  };
}

export async function sendFunnelEvent(
  payload: FunnelEventPayload,
  options?: SendFunnelEventOptions,
) {
  try {
    const res = await fetch(`${apiBaseUrl()}/api/funnel/event`, {
      method: "POST",
      headers: requestHeaders(),
      body: JSON.stringify({
        type: payload.type,
        email: payload.email,
        route:
          payload.route ||
          (typeof window !== "undefined" ? window.location.pathname : ""),
        source: payload.source || "frontend",
        reason: payload.reason || "",
        session_id: payload.sessionId || "",
        timestamp: payload.timestamp || new Date().toISOString(),
        meta: payload.meta,
      }),
      keepalive: true,
    });

    if (!res.ok) {
      throw new Error(`Funnel event failed (${res.status})`);
    }

    return true;
  } catch (error) {
    if (options?.throwOnError) {
      throw error;
    }
    return false;
  }
}

export async function fetchFunnelSummary(): Promise<FunnelSummary> {
  const res = await fetch(`${apiBaseUrl()}/api/admin/funnel/summary`, {
    headers: requestHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Failed to load funnel summary (${res.status})`);
  }
  return res.json();
}

export async function fetchRecentFunnelEvents(
  limit = 20,
): Promise<FunnelEvent[]> {
  const res = await fetch(
    `${apiBaseUrl()}/api/admin/funnel/recent?limit=${limit}`,
    {
      headers: requestHeaders(),
    },
  );
  if (!res.ok) {
    throw new Error(`Failed to load funnel events (${res.status})`);
  }
  return res.json();
}

export async function sendFunnelTestEmail(
  toEmail?: string,
): Promise<{ status: string; email_id?: string }> {
  const res = await fetch(`${apiBaseUrl()}/api/admin/funnel/test-email`, {
    method: "POST",
    headers: requestHeaders(),
    body: JSON.stringify({
      to_email: toEmail || "",
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to send test email (${res.status})`);
  }
  return res.json();
}
